import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  documentId,
  type QueryConstraint,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../lib/firebase';
import { resolveUploadContentType } from '../utils/uploadContentType';
import { sortQuoteVersionsDesc } from '../utils/quoteVersions';
import { chunkForInQuery } from '../utils/firestoreInChunks';
import { customerService } from '../modules/customers/services/customerService';
import { sumCreditablePayments as sharedSumCreditablePayments } from '../../shared/depositGate';
import type { QuotationViewResponse } from '../../shared/quotationView';
import {
  buildReassignPatch,
  collectLinkedProjectIds,
  type DirectoryUserLike,
  type ReassignTarget,
} from '../utils/quoteRepReassign';
import { fetchOrder, updateOrder } from '../modules/orders/services/orderEntityService';
import type {
  LostReason,
  ManualPaymentDetails,
  PaymentLedgerEntry,
  QuoteListLike,
  QuotePayload,
  QuoteSummary,
} from '../types/quote';

/**
 * The quote document types now live with the other shared entity types
 * (`src/types/quote.ts`, backlog #113): a quote is read by orders,
 * measurements, deliveries, requisitions and every customer-facing document,
 * so the shape is shared and only the Firestore behaviour belongs here.
 * Re-exported so every historical import path keeps working; new code imports
 * them from `src/types`.
 */
export type {
  AddonItem,
  CustomerInfo,
  LineItem,
  LostReason,
  ManualPaymentDetails,
  PaymentLedgerEntry,
  PaymentMethod,
  PaymentSource,
  QuoteListLike,
  QuotePayload,
  QuoteSummary,
} from '../types/quote';

/** Sum the amount actually creditable toward the order total — CC surcharges
 * are excluded so they don't accidentally satisfy the deposit threshold.
 *
 * Thin wrapper that delegates to the pure helper in `depositGate` so the rule
 * stays in one place. Kept as a re-export from `quoteService` because callers
 * pass the legacy `PaymentLedgerEntry` shape and importing from here is the
 * documented surface. */
export const sumCreditablePayments = (ledger: PaymentLedgerEntry[] | undefined): number =>
  sharedSumCreditablePayments(ledger);

/** Statuses a quote holds once it has an order / money. Used by PaymentsView
 *  to fetch confirmed-but-unpaid quotes (no ledger yet) alongside hasLedger. */
export const MONEY_STATUSES: readonly QuotePayload['status'][] = [
  'CLIENT_CONFIRMED',
  'PARTIALLY_PAID',
  'FULLY_PAID',
  'PAYMENT_RECEIVED',
];

const recordStaffPaymentCallable = httpsCallable<
  { quoteId: string; entry: PaymentLedgerEntry },
  QuotePayload
>(functions, 'recordStaffPayment');

export const processFractionalPayment = async (
  file: File | null,
  amount: number,
  /** Only `id` is read here — list-shaped so a `quoteSummaries` row works. */
  quote: Pick<QuoteListLike, 'id'>,
  details: ManualPaymentDetails,
): Promise<QuotePayload> => {
  // Money writes have exactly ONE implementation: the recordStaffPayment
  // Cloud Function (run locally via `npm run dev:emu`).
  // The slip is recorded by its Storage PATH, never a download URL — a URL is
  // a bearer credential (backlog #155); the CF refuses one.
  let receiptPath = '';
  if (file) {
    // Resolve a contentType the storage.rules allowlist accepts — a File with
    // a blank `.type` would otherwise upload as octet-stream and be rejected.
    const contentType = resolveUploadContentType(file);
    if (!contentType) {
      throw new Error(
        'Unsupported receipt file type. Upload a PDF or an image (PNG, JPG, WEBP, HEIC, or GIF).',
      );
    }
    const safeFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    receiptPath = `receipts/${quote.id}/${Date.now()}_${safeFilename}`;
    await uploadBytes(ref(storage, receiptPath), file, { contentType });
  }

  const entry: PaymentLedgerEntry = {
    id: details.entryId ?? crypto.randomUUID(),
    amount,
    ...(receiptPath ? { receiptPath } : {}),
    timestamp: Date.now(),
    method: details.method,
    ...(details.note ? { note: details.note } : {}),
  };

  const res = await recordStaffPaymentCallable({ quoteId: quote.id, entry });
  return res.data;
};

const adminAdjustLedgerCallable = httpsCallable<
  {
    quoteId: string;
    ledger: PaymentLedgerEntry[];
    reason: string;
    statusOverride?: QuotePayload['status'];
  },
  QuotePayload
>(functions, 'adminAdjustLedger');

/**
 * Audited admin correction of a quote's payment ledger. The strict `/quotes`
 * rule lockdown blocks ALL client-SDK writes to paymentLedger / status /
 * totals (CLAUDE.md "Security Review Notes" #1), so corrections — removing a
 * mistaken entry, fixing an amount, forcing a dispute reversal — must go
 * through the `adminAdjustLedger` Cloud Function, which writes a `/audit`
 * record atomically with the change. `ledger` is the FULL replacement set,
 * not a delta. Admin-only (enforced server-side).
 */
export const adminAdjustLedger = async (
  quoteId: string,
  ledger: PaymentLedgerEntry[],
  reason: string,
  statusOverride?: QuotePayload['status'],
): Promise<QuotePayload> => {
  const res = await adminAdjustLedgerCallable({
    quoteId,
    ledger,
    reason,
    ...(statusOverride ? { statusOverride } : {}),
  });
  return res.data;
};

export const saveQuote = async (quote: QuotePayload): Promise<void> => {
  // A quote is NOT auto-linked to an order. An order is created per accepted
  // quote at confirmation (confirmQuote), keyed to the quote itself — never by
  // mobile, which is unreliable as an engagement key (two distinct customers
  // can share one number). Mobile stays for customer-record dedup only. See
  // docs/superpowers/specs/2026-06-16-one-order-per-quote-design.md.
  const qRef = doc(db, 'quotes', quote.id);
  const now = new Date().toISOString();

  // Redact highly sensitive wholesale rates before saving to Firestore
  // to prevent public exposure on the shareable quote link
  const redactedLineItems = (quote.cart || []).map((item) => {
    const sanitizedItem = { ...item };
    if (sanitizedItem.product && typeof sanitizedItem.product === 'object') {
      const { wholesale_rate: _, ...safeProduct } = sanitizedItem.product;
      sanitizedItem.product = safeProduct;
    }
    if (sanitizedItem.addons) {
      sanitizedItem.addons = sanitizedItem.addons.map((addon) => {
        if (addon.product && typeof addon.product === 'object') {
          const { wholesale_rate: __, ...safeAddonProduct } = addon.product;
          return { ...addon, product: safeAddonProduct };
        }
        return addon;
      });
    }
    return sanitizedItem;
  });

  await setDoc(qRef, {
    ...quote,
    cart: redactedLineItems,
    metadata: {
      ...quote.metadata,
      createdAt: quote.metadata?.createdAt ?? now,
      updatedAt: now,
    },
  });

  // Fire-and-forget: the quote is already persisted above, and customer sync is
  // best-effort bookkeeping whose result we don't use and whose errors are
  // already swallowed internally (prod routes through the syncCustomer Cloud
  // Function). Awaiting it only added a second CF round trip — including a
  // possible asia-southeast1 cold start — to the perceived save latency. Detach
  // it so the rep sees the saved quote immediately; the customer record settles
  // asynchronously. The defensive .catch keeps the offline-dev localStorage path
  // from ever surfacing an unhandled rejection.
  void customerService.syncCustomerFromEntity(quote.customer).catch((e) => {
    console.warn('[saveQuote] background customer sync failed (non-blocking):', e);
  });
};

/** Every version in a quote's chain (same rootQuoteId), newest first. Includes
 * the root doc itself even when it predates the rootQuoteId stamp (legacy v1
 * docs). Failures return [] — this backs a display-only history panel, and a
 * role without quote-list permission simply sees no version history.
 *
 * Generic over the caller's own shape so a LIST row (a `quoteSummaries`
 * document, which has no `cart`) can seed the lookup: the fetched siblings are
 * always full quotes, and the only entry that can be list-shaped is the
 * caller's own object in the "not in its own chain" fallback below. A caller
 * passing a `QuotePayload` still gets `QuotePayload[]` — the union collapses. */
export const fetchQuoteVersions = async <Q extends QuoteListLike>(
  quote: Q,
): Promise<(QuotePayload | Q)[]> => {
  const rootId = quote.metadata?.rootQuoteId || quote.id;
  try {
    const q = query(collection(db, 'quotes'), where('metadata.rootQuoteId', '==', rootId));
    const snaps = await getDocs(q);
    const versions: (QuotePayload | Q)[] = [];
    snaps.forEach((docSnap) => {
      versions.push({ ...(docSnap.data() as QuotePayload), id: docSnap.id });
    });
    // Legacy root docs may lack the rootQuoteId stamp and miss the query.
    if (!versions.some((v) => v.id === rootId)) {
      const root = await fetchQuote(rootId);
      if (root) versions.push({ ...root, id: rootId });
    }
    // The caller's quote is always part of its own chain.
    if (!versions.some((v) => v.id === quote.id)) versions.push(quote);
    return sortQuoteVersionsDesc(versions);
  } catch (err) {
    console.warn('fetchQuoteVersions failed; hiding version history', err);
    return [];
  }
};

export const fetchQuote = async (quoteId: string): Promise<QuotePayload | null> => {
  try {
    const qRef = doc(db, 'quotes', quoteId);
    const snap = await getDoc(qRef);
    if (snap.exists()) {
      return snap.data() as QuotePayload;
    }
    return null;
  } catch (error) {
    console.error('Failed to retrieve quotation from Firestore', error);
    throw error;
  }
};

/**
 * The customer's quotation — the allow-listed view from the public
 * `getQuotationView` callable (spec 2026-09-29). Every customer surface reads
 * this, never `fetchQuote`: after PR-C the raw `get` is internal-only.
 * `not-found` and `invalid-argument` (a malformed link / quote id) → null, so
 * both read "Quote not found or invalid link."; anything else throws.
 */
const getQuotationViewCallable = httpsCallable<
  { quoteId: string; includeNewerVersion?: boolean; paymentIntentId?: string },
  QuotationViewResponse
>(functions, 'getQuotationView');

export const fetchQuotationView = async (
  quoteId: string,
  opts: { includeNewerVersion?: boolean; paymentIntentId?: string } = {},
): Promise<QuotationViewResponse | null> => {
  try {
    const res = await getQuotationViewCallable({
      quoteId,
      ...(opts.includeNewerVersion ? { includeNewerVersion: true } : {}),
      ...(opts.paymentIntentId ? { paymentIntentId: opts.paymentIntentId } : {}),
    });
    return res.data;
  } catch (e) {
    const code = typeof e === 'object' && e !== null ? (e as { code?: unknown }).code : undefined;
    if (code === 'functions/not-found' || code === 'functions/invalid-argument') return null;
    throw e;
  }
};

const confirmQuoteCallable = httpsCallable<
  { quoteId: string },
  { ok: true; quoteId: string; alreadyConfirmed: boolean }
>(functions, 'confirmQuote');

export const confirmQuote = async (quoteId: string): Promise<boolean> => {
  // Customers are unauthenticated, so the SENT → CLIENT_CONFIRMED
  // transition runs through a Cloud Function. The Firestore rule for public
  // updates is locked to `if false` — the only path to confirmation is this
  // callable, which validates status server-side and stamps confirmedAt with
  // serverTimestamp. UUID quote-id entropy remains the access control.
  await confirmQuoteCallable({ quoteId });
  return true;
};

/**
 * The three flags a customer can record from the public quote link.
 *
 * MIRROR of `QUOTE_CUSTOMER_INTENTS` in
 * `functions/src/transitions/quoteCustomerIntent.ts`. `shared/` is imported by both halves; the copy direction used to be
 * `src/` → `functions/`, never the reverse, so this cannot be an import —
 * it is parity-tested against the real server set in
 * `functions/src/transitions/__tests__/quoteCustomerIntentParity.test.ts`.
 */
export type QuoteCustomerIntentName = 'TERMS_ACCEPTED' | 'ACCEPT_OFFLINE' | 'REQUEST_UPDATE';

/**
 * Exhaustiveness bridge (council finding B1): every production call site is
 * typed against the UNION above, not this array — the array had zero
 * production consumers, so the parity test comparing only the array let the
 * union and the array silently diverge. Widen the union alone, without
 * adding a matching key here, and this object literal fails to typecheck
 * ("Property '<NEW>' is missing in type ... but required in type
 * 'Record<QuoteCustomerIntentName, true>'") — so a widened union that the
 * array doesn't cover is now a compile error, not a green parity test next
 * to a public callable that rejects the new value with a raw
 * `invalid-argument` in a customer's face.
 */
const QUOTE_CUSTOMER_INTENT_RECORD: Record<QuoteCustomerIntentName, true> = {
  TERMS_ACCEPTED: true,
  ACCEPT_OFFLINE: true,
  REQUEST_UPDATE: true,
};

export const QUOTE_CUSTOMER_INTENT_NAMES: readonly QuoteCustomerIntentName[] = Object.keys(
  QUOTE_CUSTOMER_INTENT_RECORD,
) as QuoteCustomerIntentName[];

/** The full shape `recordQuoteCustomerIntent` returns — used for BOTH the
 *  callable's declared response type and the wrapper's return type, so they
 *  cannot drift apart the way the earlier hand-picked-subset return did
 *  (council finding B3). */
interface RecordQuoteCustomerIntentResult {
  ok: true;
  quoteId: string;
  intent: QuoteCustomerIntentName;
  /** False when every stamp this intent would write was already present. */
  written: boolean;
  acceptedAt: string | null;
  termsAcceptedAt: string | null;
  /** Which version of TermsAndConditions.tsx's clause text termsAcceptedAt
   *  corresponds to. Null iff termsAcceptedAt is null. */
  termsVersion: string | null;
  updateRequestedAt: string | null;
}

const recordQuoteCustomerIntentCallable = httpsCallable<
  { quoteId: string; intent: QuoteCustomerIntentName },
  RecordQuoteCustomerIntentResult
>(functions, 'recordQuoteCustomerIntent');

/**
 * Record a customer flag on a SENT quotation. Changes no status and creates
 * nothing — payment (or the audited ADMIN exception) is the only route to an
 * order. Throws `failed-precondition` for a non-SENT or expired quote; the
 * caller decides whether that is worth surfacing.
 *
 * Returns the callable's response WHOLESALE, not a hand-picked subset — no
 * planned caller uses any of it today, and returning everything keeps the
 * declared type an honest description of what the server actually sends
 * back rather than an arbitrary guess at what a future caller might want.
 */
export const recordQuoteCustomerIntent = async (
  quoteId: string,
  intent: QuoteCustomerIntentName,
): Promise<RecordQuoteCustomerIntentResult> => {
  const res = await recordQuoteCustomerIntentCallable({ quoteId, intent });
  return res.data;
};

/**
 * The response shape this client TRUSTS `adminConfirmQuote` to return.
 *
 * Must stay structurally identical to the REAL server type,
 * `AdminConfirmQuoteResult` in
 * `functions/src/transitions/adminConfirmQuote.ts` — `httpsCallable` performs
 * no runtime validation, so a drift here is silent (Lesson #75, one field
 * over: a renamed/missing field reads as `undefined` at a type this file
 * trusts as `string`). There is no shared import across the boundary
 * (`functions/tsconfig.json`'s `rootDir: "src"` rejects an import back out to
 * this file from anything functions actually compiles, and the shared bundle only
 * ever copies `src/` → `functions/`), so the two declarations are pinned
 * equal at runtime instead, by
 * `functions/src/transitions/__tests__/adminConfirmQuoteResultParity.test.ts`,
 * which imports the server's real result-builder AND the two example values
 * below and compares them structurally.
 */
export interface AdminConfirmQuoteClientResult {
  ok: true;
  quoteId: string;
  projectId: string;
  ocNumber?: string;
  retried: boolean;
}

/**
 * Canonical examples of this client's trusted shape — used ONLY by
 * `adminConfirmQuoteResultParity.test.ts`. Not hand-copied into that test
 * file (a hand-copy would drift silently and catch nothing, the exact defect
 * this pair exists to prevent one level up): the test imports these values
 * directly, so they are what actually gets compared against the server's real
 * output. One WITH the optional `ocNumber`, one WITHOUT, so both branches of
 * its optionality are exercised.
 */
export const ADMIN_CONFIRM_QUOTE_CLIENT_RESULT_EXAMPLES: {
  withOcNumber: AdminConfirmQuoteClientResult;
  withoutOcNumber: AdminConfirmQuoteClientResult;
} = {
  withOcNumber: {
    ok: true,
    quoteId: 'example-quote',
    projectId: 'example-project',
    ocNumber: 'OC-EXAMPLE',
    retried: false,
  },
  withoutOcNumber: {
    ok: true,
    quoteId: 'example-quote',
    projectId: 'example-project',
    retried: true,
  },
};

const adminConfirmQuoteCallable = httpsCallable<
  { quoteId: string; reason: string },
  AdminConfirmQuoteClientResult
>(functions, 'adminConfirmQuote');

/**
 * ADMIN-only confirm-without-payment (spec §3.7) — for a B2B customer paying
 * on invoice terms against a PO. Mints a real order + OC with NO money behind
 * it, born `AWAITING_DEPOSIT`, and writes an `/audit` row naming who did it and
 * why. The reason is mandatory server-side; do not call this with a placeholder.
 *
 * It exists so the honest version of "this customer has terms" stays available.
 * Without it the first person to meet a real credit-terms case would record a
 * payment that has not arrived — which corrupts the ledger, the Payments view
 * and the sales report at once, and is invisible.
 */
export const adminConfirmQuote = async (
  quoteId: string,
  reason: string,
): Promise<{ projectId: string; ocNumber?: string }> => {
  const res = await adminConfirmQuoteCallable({ quoteId, reason });
  return { projectId: res.data.projectId, ocNumber: res.data.ocNumber };
};

/** Mark a quote as SUPERSEDED. Used when a new version is spawned from an
 * already-SENT or CLIENT_CONFIRMED quote — the prior doc stays in Firestore
 * for audit but is hidden from the active workflow. Fire-and-forget at the
 * call site; permission denials in dev mode are swallowed because the new
 * version still saves successfully. */
export const markQuoteSuperseded = async (quoteId: string): Promise<void> => {
  try {
    const ref = doc(db, 'quotes', quoteId);
    await updateDoc(ref, { status: 'SUPERSEDED' });
  } catch (err) {
    console.warn('markQuoteSuperseded failed; new version still saved', err);
  }
};

/**
 * Pre-sale sales transition (quote/order separation, Slice 3): Mark Lost /
 * Put On Hold / Reopen. Status-only plus the `lost*` / `onHold*` audit fields —
 * it NEVER touches `paymentLedger` / `totals` / a money status, so it rides the
 * plain client-SDK `updateDoc` path (gated by `quoteSalesTransitionOk` in
 * firestore.rules), NOT a money Cloud Function. The patch shape is built by the
 * pure, unit-tested `buildQuoteSalesTransitionPatch` so it can never drift into
 * a money field.
 */
export type QuoteSalesTransitionInput =
  | { status: 'LOST'; lostReason: LostReason; lostNote?: string }
  | { status: 'ON_HOLD'; onHoldFollowUpAt?: string | null }
  | { status: 'DRAFT' };

export const buildQuoteSalesTransitionPatch = (
  input: QuoteSalesTransitionInput,
  ctx: { actorEmail: string; nowIso: string },
): Record<string, string> => {
  switch (input.status) {
    case 'LOST':
      return {
        status: 'LOST',
        lostReason: input.lostReason,
        lostAt: ctx.nowIso,
        lostBy: ctx.actorEmail,
        // spread-to-omit so Firestore never receives `undefined` (Lesson #10)
        ...(input.lostNote ? { lostNote: input.lostNote } : {}),
      };
    case 'ON_HOLD':
      return {
        status: 'ON_HOLD',
        onHoldAt: ctx.nowIso,
        onHoldBy: ctx.actorEmail,
        ...(input.onHoldFollowUpAt ? { onHoldFollowUpAt: input.onHoldFollowUpAt } : {}),
      };
    case 'DRAFT':
      return { status: 'DRAFT' };
  }
};

export const applyQuoteSalesTransition = async (
  quoteId: string,
  input: QuoteSalesTransitionInput,
  actorEmail: string,
): Promise<void> => {
  const patch = buildQuoteSalesTransitionPatch(input, {
    actorEmail: (actorEmail || 'unknown').toLowerCase(),
    nowIso: new Date().toISOString(),
  });
  const ref = doc(db, 'quotes', quoteId);
  await updateDoc(ref, patch);
};

/**
 * Roles for which this fetch returns EVERY rep's quotes rather than narrowing
 * to `metadata.createdBy == userEmail`.
 *
 * This is a UI-convenience scope, not a security boundary: `firestore.rules`
 * already grants the list to `isStaffOrFinance() || isPurchaser() ||
 * isSalesManager()` and its own comment says "FINANCE and PURCHASER see
 * everything by design". Every role below reads all quotes elsewhere in the app
 * today — FINANCE through the Payments view, FINANCE_MANAGER through the Work
 * Order Register, SALES_MANAGER through the Sales report.
 *
 * FINANCE / FINANCE_MANAGER were previously absent here, and the knowledge was
 * pushed onto callers instead: PaymentsView mapped `FINANCE → 'ADMIN'` inline
 * (Lesson #29), SalesReportView and WorkOrderRegisterView pass a literal
 * `'ADMIN'`. Every caller that did NOT know the trick silently got an empty
 * list for a finance user — the quotes dashboard and the Orders list both did,
 * which is how a finance user ended up with no route to the payment logger.
 * Centralising it here is the fix.
 *
 * `SalesReportView` and `WorkOrderRegisterView` still pass a literal `'ADMIN'`,
 * and that is now purely DEFENSIVE, not load-bearing: both of their route
 * cohorts (`ADMIN_SALES_MANAGER` and `ADMIN_FINANCE`) are already entirely
 * inside this set, so passing `role` would behave identically today. Neither
 * `/staff/reports` nor `/staff/work-order-register` is in
 * `src/lib/accessSurfaces.ts`, so `extraAccess` cannot grant them to some
 * other role — the literal simply guarantees a whole-book report stays a
 * whole-book report if that ever changes. (`/staff/payments` IS grantable, and
 * `PaymentsView` was switched to pass `role`; that is not a regression, since
 * the inline map it replaced special-cased FINANCE only and narrowed every
 * other grantee exactly the same way.)
 *
 * PURCHASER is deliberately NOT included. The rule layer would allow it, and
 * their empty quotes dashboard has the same shape as the finance one, but
 * whether procurement should see every rep's pipeline is a product decision
 * nobody has made — raise it rather than widening it here.
 */
const CROSS_REP_QUOTE_READ_ROLES: ReadonlySet<string> = new Set([
  'ADMIN',
  'SALES_MANAGER',
  'FINANCE',
  'FINANCE_MANAGER',
]);

/**
 * Takes `string | null | undefined` rather than `Role` — unlike its siblings
 * `canRoleRecordStaffPayment` / `showsQuoteAuthorActions`, which take `Role`.
 * Not an oversight: `fetchAllQuotations` is typed `role: string | null` (call
 * sites pass a literal `'ADMIN'`), so a `Role` parameter here would force a
 * cast at the predicate's primary caller. The set is keyed by string either
 * way, and the tests cover the malformed inputs (`''`, `'finance'`, null).
 */
export const hasCrossRepQuoteRead = (role: string | null | undefined): boolean =>
  typeof role === 'string' && CROSS_REP_QUOTE_READ_ROLES.has(role);

const SUMMARIES = 'quoteSummaries';

export interface FetchQuotationsOptions {
  /**
   * Restrict to these statuses (one Firestore `in` clause, so ≤30). Omit to
   * fetch every status. The quotes dashboard uses this to load the archived
   * statuses lazily (backlog #78); surfaces that must see EVERY quote — the
   * Payments view, where quarantined money can sit on a SUPERSEDED / LOST quote
   * with no status move (Lesson #74) — must NOT pass it.
   */
  statuses?: readonly string[];
  /**
   * `'own'` forces the `metadata.createdBy == self` scope even for a cross-rep
   * role. The StaffDashboard uses it: that page is "my work" for everyone, and
   * it joins the few other-rep quotes it needs by id (`fetchQuotesByIds`)
   * instead of reading the whole collection.
   */
  scope?: 'role' | 'own';
  /** ISO string; only quotes with `metadata.createdAt >= createdAfter`. */
  createdAfter?: string;
}

const sortSummariesDesc = (rows: QuoteSummary[]): QuoteSummary[] =>
  rows.sort((a, b) => {
    const da = a.metadata?.createdAt ? new Date(a.metadata.createdAt).getTime() : 0;
    const dbTime = b.metadata?.createdAt ? new Date(b.metadata.createdAt).getTime() : 0;
    return dbTime - da;
  });

/**
 * List quotes for a surface — reads the `quoteSummaries` mirror, never the
 * 6.9 KB quote documents (spec 2026-09-04). `createdAfter` + `statuses`
 * together are served by the composite indexes in firestore.indexes.json;
 * ANY OTHER combination must be probed on prod before it ships (Lesson #81).
 */
export const fetchAllQuotations = async (
  userEmail: string,
  role: string | null,
  opts: FetchQuotationsOptions = {},
): Promise<QuoteSummary[]> => {
  const ref = collection(db, SUMMARIES);
  const constraints: QueryConstraint[] = [];
  // A plain SALES rep is scoped to their own quotes; the oversight and
  // back-office roles above see every rep's (unless the caller opts out).
  if (opts.scope === 'own' || !hasCrossRepQuoteRead(role)) {
    constraints.push(where('metadata.createdBy', '==', userEmail));
  }
  if (opts.statuses?.length) constraints.push(where('status', 'in', [...opts.statuses]));
  if (opts.createdAfter) {
    constraints.push(where('metadata.createdAt', '>=', opts.createdAfter));
    // Required to match the deployed DESC composite indexes: a range filter
    // with no explicit orderBy makes Firestore order implicitly by the range
    // field ASCENDING, which demands a `status ASC, metadata.createdAt ASC`
    // index that does not exist. Probed on prod (2026-09-07) — this exact
    // shape needs the `orderBy` to avoid FAILED_PRECONDITION.
    constraints.push(orderBy('metadata.createdAt', 'desc'));
  }
  const snap = await getDocs(constraints.length ? query(ref, ...constraints) : ref);
  return sortSummariesDesc(snap.docs.map((d) => d.data() as QuoteSummary));
};

/** Summaries by quote id — `documentId() in`, chunks of 30, in parallel. */
export const fetchQuotesByIds = async (ids: readonly string[]): Promise<QuoteSummary[]> => {
  const chunks = chunkForInQuery(ids);
  if (chunks.length === 0) return [];
  const ref = collection(db, SUMMARIES);
  const snaps = await Promise.all(
    chunks.map((chunk) => getDocs(query(ref, where(documentId(), 'in', chunk)))),
  );
  return sortSummariesDesc(snaps.flatMap((s) => s.docs.map((d) => d.data() as QuoteSummary)));
};

/**
 * Search across ALL summaries by one word-prefix token (spec §7). For a SALES
 * rep pass `ownerEmail` so the scoping stays server-side (equality +
 * array-contains — probe confirmed no composite index is needed; see PR B).
 */
export const fetchQuoteSummariesByToken = async (
  token: string,
  ownerEmail?: string,
): Promise<QuoteSummary[]> => {
  const t = token.trim().toLowerCase();
  if (t.length < 2) return [];
  const constraints: QueryConstraint[] = [where('searchTokens', 'array-contains', t)];
  if (ownerEmail) constraints.push(where('metadata.createdBy', '==', ownerEmail));
  const snap = await getDocs(query(collection(db, SUMMARIES), ...constraints));
  return sortSummariesDesc(snap.docs.map((d) => d.data() as QuoteSummary));
};

/**
 * Everything PaymentsView needs, without reading the whole collection:
 * every quote holding money (`hasLedger`, quarantined included — Lesson #74),
 * every quote in a money status (confirmed-but-unpaid rows have no ledger
 * yet), and the active quote of every order (the Orders / Invoices joins).
 */
export const fetchPaymentsViewSummaries = async (
  orderQuoteIds: readonly string[],
): Promise<QuoteSummary[]> => {
  const ref = collection(db, SUMMARIES);
  const [withLedger, moneyStatus, byIds] = await Promise.all([
    getDocs(query(ref, where('hasLedger', '==', true))),
    getDocs(query(ref, where('status', 'in', [...MONEY_STATUSES]))),
    fetchQuotesByIds(orderQuoteIds),
  ]);
  const byId = new Map<string, QuoteSummary>();
  for (const s of [...withLedger.docs, ...moneyStatus.docs])
    byId.set(s.id, s.data() as QuoteSummary);
  for (const s of byIds) byId.set(s.id, s);
  return sortSummariesDesc([...byId.values()]);
};

export type SendChannel = 'whatsapp' | 'email';
export interface SendChannelResult {
  channel: SendChannel;
  ok: boolean;
  /** Present for WhatsApp — the wa.me URL the client should open. */
  openUrl?: string | null;
  message?: string;
}
export interface SendQuoteResult {
  ok: boolean;
  results: SendChannelResult[];
  /** Present only when a target was sent: the stored request, or null when
   *  nothing was stored. Absent from an older Cloud Function (SF8). */
  paymentRequest?: { targetPercent: number; requestedAmount: number } | null;
  requestStored?: boolean;
}

/** Send a saved quote to the customer via WhatsApp and/or email — the
 * `sendQuoteToCustomer` Cloud Function builds the message, returns the
 * wa.me deep link, and marks DRAFT → SENT on first success. A post-sale send
 * may carry the rep's progress target (spec 2026-10-02). */
export const sendQuoteToCustomer = async (
  quoteId: string,
  channels: SendChannel[],
  targetPercent?: number,
): Promise<SendQuoteResult> => {
  const callable = httpsCallable<
    { quoteId: string; channels: SendChannel[]; targetPercent?: number },
    SendQuoteResult
  >(functions, 'sendQuoteToCustomer');
  const res = await callable({
    quoteId,
    channels,
    // Load-bearing spread: the callable SDK encodes `undefined` as `null`, and the
    // server refuses `targetPercent: null` with invalid-argument — so omit the key.
    ...(targetPercent !== undefined ? { targetPercent } : {}),
  });
  return res.data;
};

/**
 * Reads the `users` collection for the reassignment rep picker. Admin-only
 * surface (the whole reassign action is admin-gated); the `/users` list rule
 * permits admin. Returns raw rows — the picker applies `selectReassignTargets`.
 */
export const fetchQuoteAuthorDirectory = async (): Promise<DirectoryUserLike[]> => {
  const snaps = await getDocs(collection(db, 'users'));
  const rows: DirectoryUserLike[] = [];
  snaps.forEach((d) => {
    const data = d.data() as { role?: string | null; fullName?: string; whatsapp?: string };
    rows.push({
      email: d.id,
      role: data.role ?? null,
      fullName: data.fullName,
      whatsapp: data.whatsapp,
    });
  });
  return rows;
};

/**
 * Reassign a quote's owning rep to `rep`, across the WHOLE version chain (same
 * rootQuoteId), and cascade to the linked order's `assignedStaff` when one
 * exists. ADMIN-only in the UI; the `/quotes` update rule already permits an
 * admin client-SDK write to the metadata fields (non-money), and `/orders`
 * update admits admin. Idempotent — a re-run converges. Money fields are never
 * written, so the totals/ledger lockdown is untouched.
 */
export const reassignQuoteRep = async (
  quote: QuoteListLike,
  rep: ReassignTarget,
  actor: string,
): Promise<void> => {
  const versions = await fetchQuoteVersions(quote);
  const targets: QuoteListLike[] = versions.length > 0 ? versions : [quote];
  const patch = buildReassignPatch(rep);

  // 1) Every version in the chain gets the new owner metadata.
  for (const v of targets) {
    await updateDoc(doc(db, 'quotes', v.id), patch);
  }

  // 2) Cascade to any linked order (visibility rides assignedStaff — Lesson #61).
  //    A chain maps to at most one order in practice; loop for safety. Skip when
  //    the order doc doesn't exist (pre-sale quotes have a projectId only after
  //    acceptance).
  for (const projectId of collectLinkedProjectIds(targets)) {
    const order = await fetchOrder(projectId);
    if (!order) continue;
    await updateOrder(projectId, { assignedStaff: [rep.email] }, actor);
  }
};
